import React, { useState, useEffect } from "react";
import { PublicLayout } from "@/components/public/PublicLayout";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { PublicMemorialsList } from "@/components/memorials/PublicMemorialsList";
import { Search as SearchIcon, SearchX } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Memorial } from "@/hooks/use-memorials";

const Search = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<Memorial[]>([]);
  const [featuredMemorials, setFeaturedMemorials] = useState<Memorial[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoadingFeatured, setIsLoadingFeatured] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchFeaturedMemorials = async () => {
      try {
        const { data, error } = await supabase
          .from('featured_memorials')
          .select(`
            memorial_id,
            display_order,
            memorial:memorials_public(
              id,
              first_name,
              last_name,
              birth_date,
              death_date,
              place_of_birth,
              place_of_death,
              profile_picture_url,
              created_at,
              plan_type,
              expires_at
            )
          `)
          .order('display_order', { ascending: true });

        if (error) {
          console.error('Error fetching featured memorials:', error);
          throw error;
        }

        // Transform the data to match the Memorial type
        const memorials = data
          .filter(item => item.memorial) // Filter out any null memorials
          .map(item => ({
            ...item.memorial,
            admin: {
              isOwner: false,
              role: 'viewer' as const
            }
          }));

        setFeaturedMemorials(memorials);
      } catch (error) {
        console.error('Error in featured memorials fetch:', error);
      } finally {
        setIsLoadingFeatured(false);
      }
    };

    fetchFeaturedMemorials();
  }, []);

  const handleSearch = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!searchTerm.trim()) return;

    setIsSearching(true);
    setHasSearched(true);

    try {
      // Search by first name or last name
      const { data, error } = await supabase
        .from('memorials_public')
        .select('*')
        .or(`first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,person2_first_name.ilike.%${searchTerm}%,person2_last_name.ilike.%${searchTerm}%`)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error searching memorials:', error);
        throw error;
      }

      // Map to Memorial type with admin role info
      const memorialsWithRole = (data || []).map(memorial => ({
        ...memorial,
        admin: {
          isOwner: false,
          role: 'viewer' as const
        }
      }));
      setSearchResults(memorialsWithRole);
    } catch (error) {
      console.error('Error during memorial search:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleViewMemorial = (memorialId: string) => {
    navigate(`/memorial/${memorialId}`);
  };

  return (
    <PublicLayout>

      {/* Hero section */}
      <div className="bg-cover bg-center relative" style={{
        backgroundImage: "url('/assets/gemini-search-bg.webp')",
        height: "300px"
      }}>
        <div className="absolute inset-0 bg-brand-primary/40" />
        <div className="relative z-10 h-full flex items-center justify-center">
          <div className="text-center px-4">
            <h1 className="text-4xl md:text-5xl font-serif text-white mb-4 drop-shadow-md">
              Find a Memorial
            </h1>
            <p className="text-white text-xl max-w-xl mx-auto drop-shadow-md">
              Search by name to visit their memorial page
            </p>
          </div>
        </div>
      </div>

      <div className="p-4 md:p-8 lg:p-12 flex-grow bg-brand-background">
        <div className="max-w-5xl mx-auto space-y-8">
          <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgba(61,53,48,0.10)] p-6 -mt-16 relative z-20">
            <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
              <label htmlFor="memorial-search" className="block text-lg font-serif text-brand-text-primary mb-3">
                Search by name
              </label>
              <div className="flex gap-2">
                <Input
                  id="memorial-search"
                  type="text"
                  placeholder="Enter first or last name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex-grow text-base rounded-xl border-brand-secondary/20"
                />
                <Button 
                  type="submit" 
                  disabled={isSearching}
                  className="bg-brand-accent hover:bg-brand-accent/90 text-white"
                >
                  {isSearching ? (
                    <div className="animate-spin h-5 w-5 border-2 border-current border-t-transparent rounded-full" />
                  ) : (
                    <>
                      <SearchIcon className="h-4 w-4 mr-2" />
                      Search
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>

          {hasSearched && (
            <div className="space-y-6 animate-fade-in">
              {searchResults.length > 0 ? (
                <div className="bg-white p-6 rounded-2xl shadow-[0_4px_20px_rgba(61,53,48,0.08)]">
                  <h2 className="text-2xl font-serif text-brand-text-primary mb-6">
                    Found {searchResults.length} {searchResults.length === 1 ? 'memorial' : 'memorials'}
                  </h2>
                  <PublicMemorialsList memorials={searchResults} onView={handleViewMemorial} />
                </div>
              ) : (
                <div className="text-center py-12 bg-white rounded-2xl shadow-[0_4px_20px_rgba(61,53,48,0.08)]">
                  <SearchX className="w-12 h-12 mx-auto text-brand-secondary/40 mb-4" />
                  <h2 className="text-2xl font-serif text-brand-text-primary mb-2">No memorials found</h2>
                  <p className="text-brand-secondary max-w-md mx-auto">
                    We couldn't find a memorial with that name. Try a different spelling or search for a partial name.
                  </p>
                </div>
              )}
            </div>
          )}

          {!hasSearched && (
            <div className="space-y-6 animate-fade-in">
              {isLoadingFeatured ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin h-8 w-8 border-2 border-brand-primary border-t-transparent rounded-full" />
                </div>
              ) : featuredMemorials.length > 0 ? (
                <div className="bg-white p-6 rounded-2xl shadow-[0_4px_20px_rgba(61,53,48,0.08)]">
                  <h2 className="text-2xl font-serif text-brand-text-primary mb-6">
                    Featured Memorials
                  </h2>
                  <PublicMemorialsList memorials={featuredMemorials} onView={handleViewMemorial} />
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-brand-secondary italic">
                    Enter a name above to begin your search
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </PublicLayout>
  );
};

export default Search;
